// SPDX-License-Identifier: AGPL-3.0-or-later

import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import Keybind from '@app/features/input/state/Keybind';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import {getLocalMicrophonePublications} from '@app/features/voice/engine/VoiceTrackPublicationUtils';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import ParticipantVolume from '@app/features/voice/state/ParticipantVolume';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import type {Room} from 'livekit-client';

const logger = new Logger('VoiceAudioManager');
const extractUserId = (identity: string): string | null => {
	const match = identity.match(/^user_(\d+)(?:_(.+))?$/);
	return match ? match[1] : null;
};
export function applyLocalAudioPreferencesForUser(userId: string, room: Room | null): void {
	if (!room) {
		logger.warn('No room');
		return;
	}
	room.remoteParticipants.forEach((p) => {
		if (extractUserId(p.identity) !== userId) return;
		try {
			ParticipantVolume.applySettingsToParticipant(p);
		} catch (error) {
			logger.warn(`Failed to apply audio preferences for user ${userId}`, {error});
		}
	});
}
export function applyAllLocalAudioPreferences(room: Room | null): void {
	if (!room) {
		logger.warn('No room');
		return;
	}
	ParticipantVolume.applySettingsToRoom(room);
}
export function isPushToTalkActive(): boolean {
	if (VoiceSettings.voiceProcessingMode === 'studio') return false;
	return Keybind.isPushToTalkEnabled();
}
export function isPushToMuteActive(): boolean {
	if (VoiceSettings.voiceProcessingMode === 'studio') return false;
	return Keybind.isPushToMuteEffective();
}
export function applyPushToTalkHold(
	held: boolean,
	room: Room | null,
	getCurrentUserVoiceState: () => VoiceState | null,
	syncVoiceState: (partial: {self_mute?: boolean}) => void,
): void {
	Keybind.setPushToTalkHeld(held);
	if (!isPushToTalkActive()) return;
	const serverVoiceState = getCurrentUserVoiceState();
	const audioState = getEffectiveAudioState({
		serverMute: serverVoiceState?.mute,
		serverDeaf: serverVoiceState?.deaf,
	});
	if (audioState.serverMute || audioState.serverDeaf || audioState.selfDeaf) return;
	if (held && LocalVoiceState.getHasUserSetMute()) {
		LocalVoiceState.clearUserSetMute();
	}
	applyLocalMuteState(!held, room, syncVoiceState);
}
export function applyPushToMuteHold(
	held: boolean,
	room: Room | null,
	getCurrentUserVoiceState: () => VoiceState | null,
	syncVoiceState: (partial: {self_mute?: boolean}) => void,
): void {
	Keybind.setPushToMuteHeld(held);
	if (!isPushToMuteActive()) return;
	const serverVoiceState = getCurrentUserVoiceState();
	const audioState = getEffectiveAudioState({
		serverMute: serverVoiceState?.mute,
		serverDeaf: serverVoiceState?.deaf,
	});
	if (audioState.serverMute || audioState.serverDeaf || audioState.selfDeaf) return;
	const baselineMute = LocalVoiceState.getSelfMute();
	const targetMute = held || baselineMute;
	const ok = setMicrophonePublicationsMuted(room, targetMute);
	if (!ok) {
		syncVoiceState({self_mute: true});
		return;
	}
	syncVoiceState({self_mute: targetMute});
}
export function handlePushToTalkModeChange(
	room: Room | null,
	getCurrentUserVoiceState: () => VoiceState | null,
	syncVoiceState: (partial: {self_mute?: boolean}) => void,
): void {
	Keybind.resetPushToTalkState();
	Keybind.resetPushToMuteState();
	const serverVoiceState = getCurrentUserVoiceState();
	const audioState = getEffectiveAudioState({
		serverMute: serverVoiceState?.mute,
		serverDeaf: serverVoiceState?.deaf,
	});
	if (audioState.serverMute || audioState.serverDeaf || audioState.selfDeaf) return;
	if (isPushToTalkActive()) {
		LocalVoiceState.clearUserSetMute();
		applyLocalMuteState(true, room, syncVoiceState);
		return;
	}
	if (!LocalVoiceState.getHasUserSetMute()) {
		applyLocalMuteState(false, room, syncVoiceState);
	}
}
function setMicrophonePublicationsMuted(room: Room | null, muted: boolean): boolean {
	if (!room?.localParticipant) return true;
	const microphonePublications = getLocalMicrophonePublications(room.localParticipant);
	if (!muted && microphonePublications.length === 0) {
		logger.debug('Skipping unmute: no audio tracks exist. Enable microphone first.');
		return false;
	}
	microphonePublications.forEach((publication) => {
		const operation = muted ? publication.mute() : publication.unmute();
		operation.catch((error) =>
			logger.error(muted ? 'Failed to mute publication' : 'Failed to unmute publication', {error}),
		);
	});
	return true;
}
export function getMuteReason(voiceState: VoiceState | null): 'guild' | 'voice_push_to_talk' | 'self' | null {
	const isGuildMuted = voiceState?.mute ?? false;
	if (isGuildMuted) return 'guild';
	const selfMuted = voiceState?.self_mute ?? LocalVoiceState.getSelfMute();
	if (isPushToTalkActive() && !Keybind.pushToTalkHeld) return 'voice_push_to_talk';
	if (selfMuted) return 'self';
	return null;
}
export function applyLocalMuteState(
	muted: boolean,
	room: Room | null,
	syncVoiceState: (partial: {self_mute?: boolean}) => void,
): void {
	const targetMute = LocalVoiceState.getSelfDeaf() ? true : muted;
	const currentMute = LocalVoiceState.getSelfMute();
	if (room?.localParticipant) {
		const microphonePublications = getLocalMicrophonePublications(room.localParticipant);
		if (!targetMute && microphonePublications.length === 0) {
			logger.debug('Skipping unmute: no audio tracks exist. Enable microphone first.');
			syncVoiceState({self_mute: true});
			return;
		}
		microphonePublications.forEach((publication) => {
			const operation = targetMute ? publication.mute() : publication.unmute();
			operation.catch((error) =>
				logger.error(targetMute ? 'Failed to mute publication' : 'Failed to unmute publication', {error}),
			);
		});
	}
	if (currentMute !== targetMute) {
		LocalVoiceState.updateSelfMute(targetMute);
		syncVoiceState({self_mute: targetMute});
	}
}
